// @systeme: interface
// « Ce que je prépare » : les rétro-plannings en cours, la date la plus proche d'abord,
// avec ce qu'il reste à faire et un signal discret si la date a bougé.
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ChevronLeft, Plus } from 'lucide-react';
import { Link as LienRouteur } from 'react-router';
import type { Retroplanning } from '@foyer/shared';
import { Chargement } from '../composants/Chargement';
import { useMessages } from '../composants/Messages';
import { api } from '../lib/api';
import { formaterJourCourt, jourDe } from '../lib/dates';
import { couleurs, tailles } from '../theme/theme';

export function resteAFaire(p: Retroplanning): number {
  return p.jalons.filter((j) => j.element.statut !== 'fait').length;
}

export function Preparations() {
  const { direErreur } = useMessages();
  const [liste, setListe] = useState<Retroplanning[] | undefined>();

  useEffect(() => {
    api.retroplannings.lister().then(setListe).catch(direErreur);
  }, [direErreur]);

  if (!liste) return <Chargement />;

  return (
    <Box sx={{ px: `${tailles.marge}px`, pt: 3 }}>
      <Button
        component={LienRouteur}
        to="/a-venir"
        startIcon={<ChevronLeft size={18} />}
        color="inherit"
        sx={{ px: 0, mb: 1 }}
      >
        À venir
      </Button>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h1" component="h1">
          Ce que je prépare
        </Typography>
        <Button
          component={LienRouteur}
          to="/a-venir/preparations/nouvelle"
          variant="contained"
          startIcon={<Plus size={18} />}
        >
          Préparer
        </Button>
      </Stack>

      {liste.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          Un anniversaire, une rentrée, un départ… Donne la date, je prévois les étapes à rebours.
        </Typography>
      ) : (
        <List disablePadding>
          {liste.map((p) => {
            const reste = resteAFaire(p);
            return (
              <ListItemButton
                key={p.id}
                component={LienRouteur}
                to={`/a-venir/preparations/${p.id}`}
                sx={{
                  bgcolor: couleurs.blanc,
                  borderRadius: `${tailles.rayon}px`,
                  mb: 1,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <ListItemText
                  primary={p.pivot.titre}
                  secondary={[
                    p.pivot.debutLe ? formaterJourCourt(jourDe(p.pivot.debutLe)) : 'sans date',
                    reste === 0
                      ? 'tout est fait'
                      : reste === 1
                        ? 'une étape à faire'
                        : `${reste} étapes à faire`,
                  ].join(' · ')}
                />
                {p.aDecaler && (
                  <Chip label="La date a bougé" size="small" color="secondary" sx={{ ml: 1 }} />
                )}
              </ListItemButton>
            );
          })}
        </List>
      )}
    </Box>
  );
}
